// Portfolio Administration v1.0 PR 6 — HTTP transport for the Waterfalls
// config editor + cross-portfolio Loan Tape page. Hooks in
// @/hooks/portfolioAdmin/usePr6 wrap these with TanStack Query.

import { api } from "../client";
import type {
  ApproveWaterfallRequest,
  CreateWaterfallDraftRequest,
  LoanTapeEntryDto,
  LoanTapePeriodViewQuery,
  RejectWaterfallRequest,
  UpdateWaterfallDraftRequest,
  WaterfallCodeSummaryDto,
  WaterfallConfigDto,
  WaterfallPriorityDetailDto,
  WaterfallPriorityExplainDto,
  WaterfallPriorityExplainRequest,
  WaterfallRunSummaryDto,
} from "./pr6Types";

// ---- waterfall config editor -------------------------------------------

export const waterfallConfigsApi = {
  summary: (runHistoryDays?: number) =>
    api.get<WaterfallCodeSummaryDto[]>(
      "/portfolio-admin/waterfalls/summary",
      runHistoryDays ? { runHistoryDays } : undefined,
    ),
  active: (code: string, asOfDate?: string) =>
    api.get<WaterfallConfigDto>(
      `/portfolio-admin/waterfalls/${code}`,
      asOfDate ? { asOfDate } : undefined,
    ),
  history: (code: string) =>
    api.get<WaterfallConfigDto[]>(
      `/portfolio-admin/waterfalls/${code}/history`,
    ),
  runs: (code: string, limit?: number) =>
    api.get<WaterfallRunSummaryDto[]>(
      `/portfolio-admin/waterfalls/${code}/runs`,
      limit ? { limit } : undefined,
    ),
  config: (configId: string) =>
    api.get<WaterfallConfigDto>(
      `/portfolio-admin/waterfalls/configs/${configId}`,
    ),
  priorityDetail: (configId: string, position: number) =>
    api.get<WaterfallPriorityDetailDto>(
      `/portfolio-admin/waterfalls/configs/${configId}/priorities/${position}/detail`,
    ),
  explainPriority: (
    configId: string,
    position: number,
    body: WaterfallPriorityExplainRequest,
  ) =>
    api.post<WaterfallPriorityExplainDto>(
      `/portfolio-admin/waterfalls/configs/${configId}/priorities/${position}/explain`,
      body,
    ),
  createDraft: (code: string, body: CreateWaterfallDraftRequest) =>
    api.post<WaterfallConfigDto>(
      `/portfolio-admin/waterfalls/${code}/drafts`,
      body,
    ),
  updateDraft: (configId: string, body: UpdateWaterfallDraftRequest) =>
    api.patch<WaterfallConfigDto>(
      `/portfolio-admin/waterfalls/configs/${configId}`,
      body,
    ),
  propose: (configId: string) =>
    api.patch<WaterfallConfigDto>(
      `/portfolio-admin/waterfalls/configs/${configId}/propose`,
    ),
  approve: (configId: string, body: ApproveWaterfallRequest) =>
    api.patch<WaterfallConfigDto>(
      `/portfolio-admin/waterfalls/configs/${configId}/approve`,
      body,
    ),
  reject: (configId: string, body: RejectWaterfallRequest) =>
    api.patch<WaterfallConfigDto>(
      `/portfolio-admin/waterfalls/configs/${configId}/reject`,
      body,
    ),
};

// ---- cross-portfolio loan tape -----------------------------------------

export const loanTapePeriodApi = {
  listForPeriod: (q: LoanTapePeriodViewQuery) => {
    const query: Record<string, unknown> = { period: q.period };
    if (q.statuses?.length) query.statuses = q.statuses;
    if (q.variance !== undefined) query.variance = q.variance;
    if (q.minDaysLate !== undefined) query.minDaysLate = q.minDaysLate;
    if (q.search) query.search = q.search;
    return api.get<LoanTapeEntryDto[]>("/portfolio-admin/loan-tape", query);
  },
  // SP-6 — lockSelected removed (legacy tape is read-only). Reads + PSA export stay.
  exportCsvUrl: (period: string) =>
    // Returns the path; the modal triggers a browser download via <a href>
    // since fetch + Blob would lose the file-download UX cues (filename,
    // progress) and the endpoint is gated by LoanTape.Export server-side.
    `${import.meta.env.VITE_API_BASE_URL ?? "/api"}/portfolio-admin/loan-tape/export/${period}`,
};
